// Auth Store - Zustand version with multi-account support (tokens persisted to Firestore + localStorage)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { firestoreService } from '../services/firestore.service';
import { useTasksStore } from './useTasksStore';
import { useAppStore } from './useAppStore';
import { useCalendarStore } from './useCalendarStore';
import { useOnboardingStore } from './useOnboardingStore';
import { useJournalStore } from '../components/Journal/useJournalStore';
import { useCanvasStore } from './useCanvasStore';
import useShareStore from './useShareStore';
import { cacheService } from '../services/cache.service';

const useAuthStore = create(persist((set, get) => ({
  currentUser: null,
  isLoading: true,
  isReauthenticating: false,

  // Token storage: { email: { accessToken, userInfo, authenticatedAt } }
  // Tokens are persisted to Firestore and restored on page load
  googleAccounts: {},

  // Primary account is the Firebase currentUser
  getPrimaryAccountEmail: () => get().currentUser?.email || null,

  setUser: (user) => set({ currentUser: user, isLoading: false }),

  setReauthenticating: (value) => set({ isReauthenticating: value }),

  // Add or update a Google account with its token (in memory only)
  setAccountToken: (email, accessToken, userInfo = {}) => {
    const accounts = get().googleAccounts;
    const updatedAccounts = {
      ...accounts,
      [email]: {
        accessToken,
        userInfo,
        authenticatedAt: Date.now(),
      },
    };
    set({ googleAccounts: updatedAccounts });
  },

  // Get token for a specific account
  getAccountToken: (email) => {
    const accounts = get().googleAccounts;
    return accounts[email]?.accessToken || null;
  },

  // Get all connected accounts (that have valid tokens in memory)
  getConnectedAccounts: () => {
    const accounts = get().googleAccounts;
    return Object.entries(accounts).map(([email, data]) => ({
      email,
      ...data.userInfo,
      authenticatedAt: data.authenticatedAt,
    }));
  },

  // Remove a specific account from memory
  removeAccount: (email) => {
    const accounts = get().googleAccounts;
    const { [email]: _removed, ...remaining } = accounts;
    set({ googleAccounts: remaining });
    return remaining;
  },

  // Get the primary account's token (for backward compatibility)
  getGoogleAccessToken: () => {
    const primaryEmail = get().getPrimaryAccountEmail();
    if (primaryEmail) {
      return get().getAccountToken(primaryEmail);
    }
    // Fallback: return first available token
    const accounts = get().googleAccounts;
    const emails = Object.keys(accounts);
    return emails.length > 0 ? accounts[emails[0]]?.accessToken : null;
  },

  // Legacy method for backward compatibility
  setGoogleAccessToken: (token) => {
    const user = get().currentUser;
    const email = user?.email;
    if (email && token) {
      get().setAccountToken(email, token, {
        name: user.displayName,
        picture: user.photoURL,
      });
    }
  },

  // Clear token for a specific account
  clearAccountToken: (email) => {
    get().removeAccount(email);
  },

  // Legacy method - clears primary account token
  clearGoogleAccessToken: () => {
    const primaryEmail = get().getPrimaryAccountEmail();
    if (primaryEmail) {
      get().removeAccount(primaryEmail);
    }
  },

  // Check if any account has a valid token in memory
  hasConnectedAccounts: () => {
    return Object.keys(get().googleAccounts).length > 0;
  },

  // Check if a specific account has a valid token in memory
  isAccountConnected: (email) => {
    return !!get().googleAccounts[email]?.accessToken;
  },

  isAuthenticated: () => !!get().currentUser,

  // Sign out - clears all in-memory tokens
  signOut: () => {
    set({
      currentUser: null,
      googleAccounts: {},
      isReauthenticating: false,
    });
  },

  // Clear all tokens (but keep user signed in)
  clearAllTokens: () => {
    set({ googleAccounts: {} });
  },
}), {
  name: 'grownups-auth-storage',
  partialize: (state) => ({
    googleAccounts: state.googleAccounts,
  }),
}));

// Initialize auth state listener
onAuthStateChanged(auth, async (user) => {
  const store = useAuthStore.getState();
  const tasksStore = useTasksStore.getState();
  const appStore = useAppStore.getState();
  const calendarStore = useCalendarStore.getState();
  const onboardingStore = useOnboardingStore.getState();
  const journalStore = useJournalStore.getState();
  const canvasStore = useCanvasStore.getState();
  const shareStore = useShareStore.getState();

  if (user) {
    // Ensure user document exists in Firestore before initializing stores
    try {
      await firestoreService.createOrUpdateUser(user);
    } catch (error) {
      console.error('Failed to create/update user document:', error);
    }

    // Fire-and-forget: Migrate external RSVP records (Phase 3)
    // If this user was a Google Calendar attendee before signing up,
    // their external RSVP records are linked to their new account.
    if (user.email) {
      import('../services/rsvp.service').then(({ migrateExternalRsvps }) => {
        migrateExternalRsvps(user.uid, user.email, {
          displayName: user.displayName,
          photoURL: user.photoURL,
        }).catch(() => {});
      }).catch(() => {});
    }

    // User is signed in - restore tokens from Firestore
    try {
      const tokens = await firestoreService.getGoogleTokens(user.uid);

      // Populate the store with restored tokens
      Object.entries(tokens).forEach(([email, tokenData]) => {
        store.setAccountToken(email, tokenData.accessToken, {
          name: user.displayName,
          picture: user.photoURL
        });
      });
    } catch (error) {
      // Silently ignore - tokens may not exist yet for new users
      // or user document hasn't been created yet
    }

    // Initialize tasks from Firestore (GRO-46)
    try {
      await tasksStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize tasks:', error);
    }

    // Initialize app preferences from Firestore (GRO-48)
    try {
      await appStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize preferences:', error);
    }

    // Initialize calendar settings from Firestore (GRO-49)
    try {
      await calendarStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize calendar settings:', error);
    }

    // Initialize profile from Firestore (GRO-50)
    try {
      await onboardingStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize profile:', error);
    }

    // Initialize journal from Firestore (GRO-52)
    try {
      await journalStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize journal:', error);
    }

    // Initialize canvas store
    try {
      canvasStore.initializeWithUser(user.uid);
    } catch (error) {
      console.error('Failed to initialize canvas:', error);
    }

    // Initialize share store (load user's RSVPs)
    try {
      await shareStore.fetchMyRsvps();
    } catch (error) {
      console.error('Failed to initialize RSVPs:', error);
    }
  } else {
    // User signed out - clear cached data
    tasksStore.clearUserData();
    appStore.clearUserData();
    calendarStore.clearUserData();
    onboardingStore.clearUserData();
    journalStore.clearUserData();
    canvasStore.clearUserData();
    shareStore.clearUserData();
    cacheService.clearAll();

    // Clear attendee sync queues (fire-and-forget import)
    import('../services/rsvp.service').then(({ clearRetryQueue }) => {
      clearRetryQueue();
    }).catch(() => {});
    import('../services/attendeeBatchSync').then(({ clearAllPendingPushes }) => {
      clearAllPendingPushes();
    }).catch(() => {});
  }

  store.setUser(user);
});

export default useAuthStore;
